import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import { Button, Select, Tag } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import Table from '../../Common/Table';
import {
  getSorterParams, isDefined,
  queryFilterParams,
} from '../../../utils';
import { useMessageError } from '../../../hooks/common';
import { TariffTableRow, useTariffsTableGet } from '../../../hooks/api/tariffs';
import DeleteTariffButton from './actions';
import { UserRole } from '../../../enums/user';
import { useAuth } from '../../../store/auth';

// function for filter by tariff name
function TariffFilterSelect(props: any) {
  const tariffsTableGet = useTariffsTableGet();

  useEffect(() => {
    if (!tariffsTableGet.data && !tariffsTableGet.loading) {
      tariffsTableGet.fetch({ page: 1, pageSize: 100 });
    }
  }, []);

  const options = tariffsTableGet.data?.data.map((t) => ({
    label: t.name,
    value: t.name,
  })) || [];

  return (
    <Select
      {...props}
      allowClear
      placeholder="Select tariff"
      options={options}
      loading={tariffsTableGet.loading}
    />
  );
}

const columns: ProColumns<TariffTableRow>[] = [
  {
    title: 'Tariff Name',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    hideInSearch: true,
    // renderFormItem: (_, { defaultRender, ...config }) => (
    //   <TariffFilterSelect {...config} />
    // ),
  },
  {
    title: 'TariffId',
    dataIndex: 'tariffId',
    key: 'tariffId',
    hideInSearch: true,
    sorter: true,
  },
  {
    title: 'Invite Friends active',
    dataIndex: 'isActive',
    key: 'isActive',
    sorter: true,
    valueType: 'select',
    valueEnum: {
      true: { text: 'Active' },
      false: { text: 'Inactive' },
    },
    render: (_, record) => (
      <Tag color={record.isActive ? 'green' : 'red'}>
        {record.isActive ? 'Active' : 'Inactive'}
      </Tag>
    ),
  },
  {
    title: 'URL landing',
    dataIndex: 'invitationPageUrl',
    key: 'invitationPageUrl',
    hideInSearch: true,
    sorter: false,
    ellipsis: true,
    render: (_, record) => record.invitationPageUrl || '—',
  },
  {
    title: 'Brevo ID',
    dataIndex: 'brevoListId',
    key: 'brevoListId',
    hideInSearch: true,
    sorter: false,
  },
  {
    title: 'Active Guaranties',
    dataIndex: 'guaranties',
    key: 'guaranties',
    hideInSearch: true,
    sorter: false,
    renderText: (_, record) => {
      const {
        basicPrice, basicPricePeriod, energyPrice, energyPricePeriod, numberOfMonths,
        tooltips,
      } = record;

      // If no fields exist → return null
      if (![
        basicPrice, basicPricePeriod, energyPrice, energyPricePeriod, tooltips?.guaranties, numberOfMonths,
      ].some(Boolean)) {
        return null;
      }

      return (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '280px',
        }}
        >
          {isDefined(basicPrice) && (
            <span>
              <strong>Basic Price:</strong>
              {' '}
              {basicPrice}
            </span>
          )}
          {isDefined(basicPricePeriod) && (
            <span>
              <strong>Basic Price Period:</strong>
              {' '}
              {basicPricePeriod}
            </span>
          )}
          {isDefined(energyPrice) && (
            <span>
              <strong>Energy Price:</strong>
              {' '}
              {energyPrice}
            </span>
          )}
          {isDefined(energyPricePeriod) && (
            <span>
              <strong>Energy Price Period:</strong>
              {' '}
              {energyPricePeriod}
            </span>
          )}
          {isDefined(numberOfMonths) && (
            <span>
              <strong>Netto-Preisgarantie Period:</strong>
              {' '}
              {numberOfMonths}
            </span>
          )}

          <br />
          {isDefined(tooltips?.guaranties?.title) && (
            <span className="clip">
              <strong>Popup title:</strong>
              {' '}
              {tooltips.guaranties.title}
            </span>
          )}
          {isDefined(tooltips?.guaranties?.description) && (
            <span>
              <strong>Popup description:</strong>
              {' '}
              <p className="clip" style={{ margin: 0 }} title={tooltips.guaranties?.description}>
                {tooltips.guaranties?.description}
              </p>
            </span>
          )}
        </div>
      );
    },
  },
  {
    title: 'Price Overview',
    dataIndex: 'priceOverview',
    key: 'priceOverview',
    hideInSearch: true,
    sorter: false,
    renderText: (_, record) => {
      const { priceOverview } = record;

      // If no fields exist → return null
      if (!Object.values(priceOverview).some(Boolean)) {
        return null;
      }

      const {
        energyPriceLabel, energyPriceValue, bonusLabel, bonusValue,
      } = priceOverview;

      return (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '220px',
        }}
        >
          {isDefined(energyPriceLabel) && isDefined(energyPriceValue) && (
            <span className="clip" title={`${energyPriceLabel}: ${energyPriceValue}`}>
              <strong>Energy price row:</strong>
              <br />
              {energyPriceLabel}
              {': '}
              {energyPriceValue}
            </span>
          )}
          <br />
          {isDefined(bonusLabel) && isDefined(bonusValue) && (
            <span className="clip" title={`${bonusLabel}: ${bonusValue}`}>
              <strong>Bonus row:</strong>
              <br />
              {bonusLabel}
              {': '}
              {bonusValue}
            </span>
          )}
        </div>
      );
    },
  },
];

function ToolBar() {
  const navigate = useNavigate();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate('create');
      }}
    >
      Add
    </Button>,
  ];
}

export default function TariffsContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.role && [UserRole.ADMIN, UserRole.ROOT].includes(user.role);
  const tariffsTableGet = useTariffsTableGet();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(Date.now());

  /** Re-render table once user?.role is resolved (e.g. after /users/me finishes), so actions column is correct. */
  useEffect(() => {
    setTableKey(Date.now());
  }, [user?.role]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<TariffTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return tariffsTableGet.fetch({
      ...newParams,
      name: newParams?.name || undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([tariffsTableGet]);

  return (
    <Table<TariffTableRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className={clsx('transparent')}
      headerTitle="List of Tariffs"
      toolBarRender={isAdmin ? ToolBar : undefined}
      request={tableRequest}
      columns={columns}
      actions={user?.role && [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT].includes(user?.role) ? [
        ['edit', ({ record }: { record: TariffTableRow }) => ({
          title: isAdmin ? 'Edit tariff' : 'View tariff',
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            navigate(`${record.id}/edit`);
          },
        })],
        [DeleteTariffButton, {
          setTableKey,
        }],
      ].filter((action) => isAdmin || action[0] === 'edit') as any : undefined}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {
          // display: 'none',
        },
      }}
      options={{
        search: {
          name: 'name',
        },
      }}
      columnsState={{
        persistenceKey: 'pro-table-tariffs',
        persistenceType: 'localStorage',
      }}
    />
  );
}
